/**
 * SIDERA — Luxury Fashion Theme JavaScript
 * Vanilla JS only — no jQuery, no frameworks
 */

'use strict';

(function () {

  // ============================================================
  // 0. LOCALIZATION HELPER
  // ============================================================
  const SIDERA_LANG = (document.documentElement.lang || 'fr').toLowerCase().indexOf('en') === 0 ? 'en' : 'fr';
  const SIDERA_T_DICT = {
    quick_add:        { fr: 'AJOUT RAPIDE',        en: 'QUICK ADD' },
    added:            { fr: 'AJOUTÉ',              en: 'ADDED' },
    add_to_cart:      { fr: 'AJOUTER AU PANIER',   en: 'ADD TO CART' },
    sold_out:         { fr: 'ÉPUISÉ',              en: 'SOLD OUT' },
    cart_add_error:   { fr: "Impossible d'ajouter cet article \u00e0 votre panier.", en: 'Sorry, we could not add this item to your cart.' },
    search_no_result: { fr: 'Aucun résultat pour', en: 'No results for' },
    search_view_all:  { fr: 'Voir tous les résultats pour', en: 'See all results for' },
    search_loading:   { fr: 'Recherche…',          en: 'Searching…' }
  };
  const SIDERA_T = (key) => (SIDERA_T_DICT[key] && SIDERA_T_DICT[key][SIDERA_LANG]) || (SIDERA_T_DICT[key] && SIDERA_T_DICT[key].fr) || key;

  // ============================================================
  // 0b. MARKET-AWARE URL HELPERS
  // ============================================================
  // Shopify.routes.root is set by Shopify to the current market's URL prefix
  // (e.g. '/en-us/' on the US market, '/' on FR primary, '/en/' on FR-EN).
  // All AJAX cart calls MUST go through the market prefix so responses use the
  // active market's presentment currency. Hitting bare '/cart.js' returns prices
  // in the shop's base currency (EUR) even for a USD/GBP shopper, which produces
  // the wrong price in the drawer and broke checkout for US/UK shoppers.
  const SIDERA_ROOT = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const SIDERA_URL = (path) => (SIDERA_ROOT().replace(/\/$/, '') + '/' + String(path).replace(/^\//, ''));

  // ============================================================
  // 1. DARK MODE
  // ============================================================
  const DarkMode = {
    KEY: 'sidera-dark-mode',

    init() {
      const saved = localStorage.getItem(this.KEY);
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const isDark = saved !== null ? saved === 'true' : prefersDark;
      this.apply(isDark, false);

      const btn = document.getElementById('dark-mode-toggle');
      if (btn) {
        btn.addEventListener('click', () => this.toggle());
        btn.setAttribute('aria-pressed', isDark.toString());
      }

      // Drawer segmented control (Light / Dark)
      const drawerToggle = document.getElementById('drawer-theme-toggle');
      if (drawerToggle) {
        drawerToggle.addEventListener('click', (e) => {
          const option = e.target.closest('[data-theme-value]');
          if (option) {
            this.apply(option.dataset.themeValue === 'dark', true);
          } else {
            this.toggle();
          }
        });
      }
      this.syncDrawer(isDark);
    },

    toggle() {
      const isDark = document.documentElement.classList.contains('dark');
      this.apply(!isDark, true);
    },

    apply(isDark, save) {
      document.documentElement.classList.toggle('dark', isDark);
      document.documentElement.classList.toggle('no-js', false);
      const btn = document.getElementById('dark-mode-toggle');
      if (btn) btn.setAttribute('aria-pressed', isDark.toString());
      if (save) localStorage.setItem(this.KEY, isDark.toString());
      this.syncDrawer(isDark);
    },

    syncDrawer(isDark) {
      const options = document.querySelectorAll('#drawer-theme-toggle [data-theme-value]');
      options.forEach(opt => {
        const active = (opt.dataset.themeValue === 'dark') === isDark;
        opt.classList.toggle('mobile-drawer__pill--active', active);
      });
    }
  };

  // ============================================================
  // 2. HEADER SCROLL BEHAVIOR
  // ============================================================
  const Header = {
    init() {
      const header = document.getElementById('site-header');
      if (!header) return;

      let lastScroll = 0;

      const onScroll = () => {
        const current = window.scrollY;
        header.classList.toggle('nav--scrolled', current > 20);
        lastScroll = current;
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  };

  // ============================================================
  // 2b. HEADER SEARCH (expand-in-place, submits to /search)
  // ============================================================
  const HeaderSearch = {
    init() {
      const form = document.getElementById('header-search');
      if (!form) return;
      const input = form.querySelector('.header-search__input');
      const toggle = form.querySelector('.header-search__toggle');
      if (!input || !toggle) return;

      // --- Predictive-search dropdown panel (created lazily) ---
      const panel = document.createElement('ul');
      panel.className = 'header-search__panel';
      panel.id = 'header-search-panel';
      panel.setAttribute('role', 'listbox');
      panel.setAttribute('aria-label', 'Search suggestions');
      panel.hidden = true;
      form.appendChild(panel);

      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-controls', 'header-search-panel');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-expanded', 'false');

      let abortCtrl = null;
      let debounceTimer = null;
      let activeIndex = -1;
      let rowEls = [];
      let lastQuery = '';

      const shopCurrency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
      const shopLocale = (document.documentElement.lang || 'en').replace('_', '-');

      // Shopify's /search/suggest.json returns product.price as a decimal
      // string in the shop's main currency unit ("515.00", not "51500").
      // Accept either a number or a string; coerce safely, then format.
      const formatPrice = (raw) => {
        if (raw == null || raw === '') return null;
        const n = Number(raw);
        if (!Number.isFinite(n) || n <= 0) return null;
        try {
          return new Intl.NumberFormat(shopLocale, {
            style: 'currency',
            currency: shopCurrency
          }).format(n);
        } catch (_) {
          return shopCurrency + ' ' + n.toFixed(2);
        }
      };

      const escapeHtml = (s) => {
        if (s == null) return '';
        return String(s)
          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      };

      const clearActive = () => {
        rowEls.forEach(el => el.removeAttribute('data-active'));
        input.removeAttribute('aria-activedescendant');
        activeIndex = -1;
      };

      const setActive = (idx) => {
        if (!rowEls.length) return;
        if (idx < 0) idx = rowEls.length - 1;
        if (idx >= rowEls.length) idx = 0;
        clearActive();
        activeIndex = idx;
        const el = rowEls[idx];
        el.setAttribute('data-active', 'true');
        if (el.id) input.setAttribute('aria-activedescendant', el.id);
        el.scrollIntoView({ block: 'nearest' });
      };

      const openPanel = () => {
        panel.hidden = false;
        form.dataset.panelOpen = 'true';
        input.setAttribute('aria-expanded', 'true');
        positionMobilePanel();
      };

      const closePanel = () => {
        form.dataset.panelOpen = 'false';
        input.setAttribute('aria-expanded', 'false');
        clearActive();
        // keep hidden true after transition so listbox is not in AT tree
        setTimeout(() => {
          if (form.dataset.panelOpen !== 'true') panel.hidden = true;
        }, 140);
      };

      const positionMobilePanel = () => {
        // On mobile the panel is position:fixed; anchor its top to the bottom
        // of the expanded search bar so it doesn't float over the viewport.
        if (window.matchMedia('(max-width: 768px)').matches) {
          const rect = form.getBoundingClientRect();
          panel.style.top = rect.bottom + 'px';
        } else {
          panel.style.top = '';
        }
      };

      const renderViewAllRow = (q) => {
        const btn = document.createElement('button');
        btn.type = 'submit';
        btn.className = 'header-search__view-all';
        btn.setAttribute('role', 'option');
        btn.id = 'hs-view-all';
        btn.textContent = SIDERA_T('search_view_all') + ' "' + q + '" →';
        return btn;
      };

      const renderStatus = (msg) => {
        panel.innerHTML = '';
        const li = document.createElement('li');
        li.className = 'header-search__status';
        li.textContent = msg;
        panel.appendChild(li);
        rowEls = [];
      };

      const renderEmpty = (q) => {
        panel.innerHTML = '';
        const li = document.createElement('li');
        li.className = 'header-search__status';
        li.textContent = SIDERA_T('search_no_result') + ' "' + q + '"';
        panel.appendChild(li);
        panel.appendChild(renderViewAllRow(q));
        rowEls = Array.from(panel.querySelectorAll('[role="option"]'));
      };

      const renderResults = (data, q) => {
        panel.innerHTML = '';
        let products = (data.resources && data.resources.results && data.resources.results.products) || [];
        // Exclude gift-with-purchase and hidden products from predictive search
        products = products.filter(p => {
          const tags = ((p.tags || []).join(' ') + ' ' + (p.handle || '')).toLowerCase();
          return !tags.includes('do-not-sell-standalone') && !tags.includes('gift-with-purchase') && !tags.includes('hidden');
        });
        const collections = (data.resources && data.resources.results && data.resources.results.collections) || [];
        const pages = (data.resources && data.resources.results && data.resources.results.pages) || [];

        const totalHits = products.length + collections.length + pages.length;
        if (totalHits === 0) {
          renderEmpty(q);
          return;
        }

        let optIdx = 0;
        const addSection = (label) => {
          const h = document.createElement('li');
          h.className = 'header-search__section-label';
          h.textContent = label;
          h.setAttribute('aria-hidden', 'true');
          panel.appendChild(h);
        };

        if (products.length) {
          addSection('Products');
          products.slice(0, 6).forEach(p => {
            const a = document.createElement('a');
            a.className = 'header-search__option';
            a.href = p.url || '#';
            a.setAttribute('role', 'option');
            a.id = 'hs-opt-' + (optIdx++);
            const imgSrc = (p.image || (p.featured_image && (p.featured_image.url || p.featured_image)) || '');
            const imgHtml = imgSrc
              ? '<img class="header-search__thumb" src="' + escapeHtml(imgSrc) + '" alt="' + escapeHtml(p.title || '') + '" loading="lazy" width="40" height="40" />'
              : '<span class="header-search__thumb header-search__thumb--empty" aria-hidden="true"></span>';
            let priceHtml = '';
            // Prefer a min/max range when variants span different prices.
            const min = Number(p.price_min);
            const max = Number(p.price_max);
            if (Number.isFinite(min) && Number.isFinite(max) && min > 0 && max > min) {
              const formatted = formatPrice(min);
              if (formatted) priceHtml = '<span class="header-search__price">à partir de ' + escapeHtml(formatted) + '</span>';
            } else {
              const formatted = formatPrice(p.price);
              if (formatted) priceHtml = '<span class="header-search__price">' + escapeHtml(formatted) + '</span>';
            }
            a.innerHTML = imgHtml +
              '<span class="header-search__title">' + escapeHtml(p.title || '') + '</span>' +
              priceHtml;
            panel.appendChild(a);
          });
        }

        if (collections.length) {
          addSection('Collections');
          collections.slice(0, 3).forEach(c => {
            const a = document.createElement('a');
            a.className = 'header-search__option';
            a.href = c.url || '#';
            a.setAttribute('role', 'option');
            a.id = 'hs-opt-' + (optIdx++);
            a.innerHTML = '<span class="header-search__thumb header-search__thumb--empty" aria-hidden="true"></span>' +
              '<span class="header-search__title">' + escapeHtml(c.title || '') + '</span>';
            panel.appendChild(a);
          });
        }

        if (pages.length) {
          addSection('Pages');
          pages.slice(0, 3).forEach(pg => {
            const a = document.createElement('a');
            a.className = 'header-search__option';
            a.href = pg.url || '#';
            a.setAttribute('role', 'option');
            a.id = 'hs-opt-' + (optIdx++);
            a.innerHTML = '<span class="header-search__thumb header-search__thumb--empty" aria-hidden="true"></span>' +
              '<span class="header-search__title">' + escapeHtml(pg.title || '') + '</span>';
            panel.appendChild(a);
          });
        }

        panel.appendChild(renderViewAllRow(q));
        rowEls = Array.from(panel.querySelectorAll('[role="option"]'));
      };

      const runQuery = (q) => {
        if (abortCtrl) abortCtrl.abort();
        abortCtrl = new AbortController();
        const url = '/search/suggest.json?q=' + encodeURIComponent(q) +
          '&resources[type]=product,collection,page' +
          '&resources[limit]=6' +
          '&resources[options][unavailable_products]=last';
        fetch(url, { signal: abortCtrl.signal, headers: { 'Accept': 'application/json' } })
          .then(r => r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))
          .then(data => {
            if (q !== lastQuery) return; // a newer query already superseded this
            renderResults(data, q);
            openPanel();
          })
          .catch(err => {
            if (err && err.name === 'AbortError') return;
            // Silent fallback: close panel, native form submit remains available
            renderStatus(SIDERA_T('search_no_result') + ' "' + q + '"');
          });
      };

      const onInput = () => {
        const q = input.value.trim();
        lastQuery = q;
        clearActive();
        if (q.length < 2) {
          closePanel();
          if (abortCtrl) abortCtrl.abort();
          return;
        }
        // Show "Searching…" immediately so the panel appears during typing
        renderStatus(SIDERA_T('search_loading'));
        openPanel();
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => runQuery(q), 200);
      };

      // --- Expand / collapse ---
      const expand = () => {
        form.dataset.expanded = 'true';
        toggle.setAttribute('aria-expanded', 'true');
        input.tabIndex = 0;
        requestAnimationFrame(() => input.focus());
      };

      const collapse = () => {
        form.dataset.expanded = 'false';
        toggle.setAttribute('aria-expanded', 'false');
        input.tabIndex = -1;
        input.blur();
        closePanel();
      };

      toggle.addEventListener('click', (e) => {
        if (form.dataset.expanded !== 'true') {
          e.preventDefault();
          expand();
          return;
        }
        if (input.value.trim() === '') {
          e.preventDefault();
          collapse();
        }
        // else: allow native submit
      });

      input.addEventListener('input', onInput);

      input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          if (form.dataset.panelOpen === 'true') {
            closePanel();
          } else {
            collapse();
          }
          return;
        }
        if (e.key === 'ArrowDown') {
          if (rowEls.length) { e.preventDefault(); setActive(activeIndex + 1); }
          return;
        }
        if (e.key === 'ArrowUp') {
          if (rowEls.length) { e.preventDefault(); setActive(activeIndex - 1); }
          return;
        }
        if (e.key === 'Enter') {
          // If a row is highlighted (and it's a link), navigate there.
          // Otherwise fall through to native form submit -> /search.
          if (activeIndex >= 0 && rowEls[activeIndex]) {
            const el = rowEls[activeIndex];
            if (el.tagName === 'A' && el.href) {
              e.preventDefault();
              window.location.href = el.href;
              return;
            }
            // view-all button: let the native submit happen
          }
        }
      });

      form.addEventListener('submit', (e) => {
        if (input.value.trim() === '') {
          e.preventDefault();
          collapse();
        } else {
          // native submit to /search?q=...; panel will be torn down on navigation
        }
      });

      document.addEventListener('click', (e) => {
        if (form.contains(e.target)) return;
        if (form.dataset.panelOpen === 'true') closePanel();
        if (form.dataset.expanded === 'true' && input.value.trim() === '') collapse();
      });

      window.addEventListener('resize', () => {
        if (form.dataset.panelOpen === 'true') positionMobilePanel();
      });
    }
  };

  // ============================================================
  // 3. MOBILE MENU
  // ============================================================
  // Mobile drawer (hamburger menu) — categories, language, currency
  // ============================================================
  const MobileDrawer = {
    init() {
      const drawer = document.getElementById('mobile-drawer');
      const openers = document.querySelectorAll('[data-mobile-drawer-open]');
      if (!drawer || !openers.length) return;

      const closers = drawer.querySelectorAll('[data-mobile-drawer-close]');
      const links = drawer.querySelectorAll('.mobile-drawer__links a');

      const open = () => {
        drawer.hidden = false;
        // Force reflow so transition triggers reliably
        void drawer.offsetWidth;
        drawer.classList.add('is-open');
        document.body.classList.add('drawer-open');
        openers.forEach(b => b.setAttribute('aria-expanded', 'true'));
      };
      const close = () => {
        drawer.classList.remove('is-open');
        document.body.classList.remove('drawer-open');
        openers.forEach(b => b.setAttribute('aria-expanded', 'false'));
        // Hide from AT after slide-out
        setTimeout(() => { if (!drawer.classList.contains('is-open')) drawer.hidden = true; }, 400);
      };

      openers.forEach(b => b.addEventListener('click', (e) => {
        e.preventDefault();
        drawer.classList.contains('is-open') ? close() : open();
      }));
      closers.forEach(c => c.addEventListener('click', close));
      // Close on link tap (drawer stays open until nav completes; close for feel)
      links.forEach(a => a.addEventListener('click', close));
      // Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
      });
    }
  };

  // ============================================================
  const MobileMenu = {
    init() {
      const toggle = document.getElementById('mobile-menu-toggle');
      const menu = document.getElementById('mobile-menu');
      if (!toggle || !menu) return;

      toggle.addEventListener('click', () => {
        const isOpen = menu.classList.contains('open');
        menu.classList.toggle('open', !isOpen);
        menu.setAttribute('aria-hidden', isOpen.toString());
        toggle.setAttribute('aria-expanded', (!isOpen).toString());
        document.body.style.overflow = isOpen ? '' : 'hidden';
      });

      const closeMenu = () => {
        menu.classList.remove('open');
        menu.setAttribute('aria-hidden', 'true');
        toggle.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
      };

      // Close on outside click
      document.addEventListener('click', (e) => {
        if (!menu.classList.contains('open')) return;
        if (!toggle.contains(e.target) && !menu.contains(e.target)) closeMenu();
      });

      // Close on Escape (a11y)
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && menu.classList.contains('open')) {
          closeMenu();
          toggle.focus();
        }
      });
    }
  };

  // ============================================================
  // 4. AUTO-GIFT: FROSTYBAG-M (1 per ready-to-wear item)
  // ============================================================
  //
  // Whenever the cart contains ready-to-wear items (products in the
  // "dresses" or "swim" collections), the cart automatically holds one
  // FROSTYBAG-M per RTW unit. The bag is free (0 EUR) and cannot be
  // manually adjusted or removed via the cart drawer UI. This is enforced
  // client-side on every cart mutation.
  //
  // Limitations: only applies to online-store direct orders. Draft orders
  // created in Shopify Admin need the bag added manually.
  //
  // Sync ONE bag variant against ONE list of trigger product IDs.
  async function syncOneBag(bagVariantId, triggerProductIds, cart) {
    if (!bagVariantId || !cart) return cart;
    var qty = (cart.items || []).reduce(function(sum, i) {
      if (i.variant_id === bagVariantId) return sum;
      return triggerProductIds.indexOf(i.product_id) !== -1 ? sum + i.quantity : sum;
    }, 0);
    var bag = (cart.items || []).find(function(i) { return i.variant_id === bagVariantId; });
    var current = bag ? bag.quantity : 0;
    if (qty === current) return cart;

    if (qty === 0 && bag) {
      var r1 = await fetch(SIDERA_URL('cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: bag.key, quantity: 0 })
      });
      return r1.json();
    }
    if (current === 0 && qty > 0) {
      await fetch(SIDERA_URL('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: bagVariantId,
            quantity: qty,
            properties: { '_auto_gift': 'true' }
          }]
        })
      });
      return (await fetch(SIDERA_URL('cart.js'), { headers: { 'Accept': 'application/json' } })).json();
    }
    var r2 = await fetch(SIDERA_URL('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: bag.key, quantity: qty })
    });
    return r2.json();
  }

  // Compute cart item count EXCLUDING the auto-gift bags. The bags stay in the
  // Shopify cart for fulfillment but must be invisible to the customer
  // everywhere in the UI — including the header badge. Keep the variant IDs in
  // sync with sections/header.liquid + sections/cart-items.liquid.
  function getVisibleItemCount(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return (cart && typeof cart.item_count === 'number') ? cart.item_count : 0;
    }
    var BAG_M = window.SIDERA_FROSTYBAG_M_VARIANT_ID || window.SIDERA_FROSTYBAG_VARIANT_ID;
    var BAG_S = window.SIDERA_FROSTYBAG_S_VARIANT_ID;
    return cart.items.reduce(function(sum, item) {
      if (item.variant_id === BAG_M || item.variant_id === BAG_S) return sum;
      return sum + (item.quantity || 0);
    }, 0);
  }
  window.getVisibleItemCount = getVisibleItemCount;

  // ============================================================
  // Free-shipping progress bar
  // ============================================================
  //
  // Displays how close the customer is to free shipping. Aligned with the
  // Shopify shipping condition: subtotal >= €200 EUR (updated 2026-09-05).
  // Per-currency thresholds sit slightly ABOVE the EUR-equivalent so the
  // drawer never over-promises when Shopify converts to base currency at
  // the checkout FX rate (+ ~10% safety margin against rate fluctuations).
  var SIDERA_SHIPPING_THRESHOLDS = {
    EUR: 200,
    USD: 220,
    GBP: 175,
    CHF: 200,
    NOK: 2300,
    AED: 850
  };

  function formatMoneyByCurrency(amount, currency) {
    var lang = document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(lang, {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 0
      }).format(amount);
    } catch (e) {
      return currency + ' ' + Math.round(amount);
    }
  }

  function updateShippingProgress(cart) {
    var container = document.querySelector('[data-shipping-progress]');
    if (!container) return;
    var label = container.querySelector('[data-shipping-progress-label]');
    var fill = container.querySelector('[data-shipping-progress-fill]');
    if (!label || !fill) return;

    if (!cart || !cart.item_count) {
      container.style.display = 'none';
      return;
    }
    container.style.display = '';

    var currency = (cart.currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR').toUpperCase();
    var threshold = SIDERA_SHIPPING_THRESHOLDS[currency] || SIDERA_SHIPPING_THRESHOLDS.EUR;
    var subtotalMinor = typeof cart.items_subtotal_price === 'number' ? cart.items_subtotal_price : (cart.total_price || 0);
    var subtotal = subtotalMinor / 100;

    var isEn = /(^|[-_])en([-_]|$)/i.test(document.documentElement.lang || '');
    var pct = Math.max(0, Math.min(100, (subtotal / threshold) * 100));
    fill.style.width = pct + '%';

    if (subtotal >= threshold) {
      label.classList.add('is-unlocked');
      label.innerHTML = isEn
        ? '✓ <strong>Free shipping unlocked</strong>'
        : '✓ <strong>Livraison offerte débloquée</strong>';
    } else {
      label.classList.remove('is-unlocked');
      var remaining = threshold - subtotal;
      var remainingFmt = formatMoneyByCurrency(remaining, currency);
      label.innerHTML = isEn
        ? 'Add <strong>' + remainingFmt + '</strong> for free shipping'
        : 'Plus que <strong>' + remainingFmt + '</strong> pour la livraison offerte';
    }
  }
  window.updateShippingProgress = updateShippingProgress;

  async function syncFrostybagQty() {
    var BAG_M = window.SIDERA_FROSTYBAG_M_VARIANT_ID || window.SIDERA_FROSTYBAG_VARIANT_ID;
    var BAG_S = window.SIDERA_FROSTYBAG_S_VARIANT_ID;
    var DRESSES = window.SIDERA_DRESS_PRODUCT_IDS || [];
    var SWIM = window.SIDERA_SWIM_PRODUCT_IDS || [];
    try {
      var cart = await (await fetch(SIDERA_URL('cart.js'), { headers: { 'Accept': 'application/json' } })).json();
      // Sync M for dresses first, then re-read cart before syncing S so we act on fresh state
      if (BAG_M && DRESSES.length) cart = await syncOneBag(BAG_M, DRESSES, cart);
      if (BAG_S && SWIM.length) {
        cart = await (await fetch(SIDERA_URL('cart.js'), { headers: { 'Accept': 'application/json' } })).json();
        cart = await syncOneBag(BAG_S, SWIM, cart);
      }
      return cart;
    } catch (e) {
      console.warn('Frostybag sync failed:', e);
      return null;
    }
  }
  window.syncFrostybagQty = syncFrostybagQty;

  // ============================================================
  // 5. CART DRAWER
  // ============================================================
  const CartDrawer = {
    el: null,
    overlay: null,
    body: null,

    init() {
      this.el = document.getElementById('cart-drawer');
      if (!this.el) return;

      this.overlay = document.getElementById('overlay');
      this.body = document.getElementById('cart-drawer-body');

      // Open triggers
      const cartBtn = document.getElementById('cart-toggle');
      if (cartBtn) cartBtn.addEventListener('click', () => this.open());

      // Close triggers
      const closeBtn = document.getElementById('cart-drawer-close');
      if (closeBtn) closeBtn.addEventListener('click', () => this.close());

      const drawerOverlay = this.el.querySelector('.cart-drawer__overlay');
      if (drawerOverlay) drawerOverlay.addEventListener('click', () => this.close());

      if (this.overlay) this.overlay.addEventListener('click', () => this.close());

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.el.classList.contains('open')) {
          this.close();
        }
      });

      // Quantity buttons (delegated)
      if (this.body) {
        this.body.addEventListener('click', (e) => {
          const qtyBtn = e.target.closest('.qty-btn');
          if (qtyBtn) this.handleQtyChange(qtyBtn);

          const removeBtn = e.target.closest('.cart-item__remove');
          if (removeBtn) this.handleRemove(removeBtn);
        });
      }

      // Quick add buttons (delegated from product grid)
      document.addEventListener('click', (e) => {
        const quickAdd = e.target.closest('.product-card__quick-add');
        if (quickAdd) {
          e.preventDefault();
          this.quickAdd(quickAdd);
        }
      });

      // Product form submission (ATC)
      const productForm = document.getElementById('product-form');
      if (productForm) {
        productForm.addEventListener('submit', (e) => {
          e.preventDefault();
          this.addToCart(productForm);
        });
      }

      // CGV / Privacy consent checkbox removed 2026-09-04 — was gating the checkout
      // button and blocking ~97% of ATC→checkout conversions (Shopify checkout has
      // its own legal footer, and Shopify Payments TOS is implicit on payment).
      // this.bindConsent();

      // Auto-sync frostybag on page load (in case cart has items but bag qty
      // is out of sync from a previous session).
      if (typeof syncFrostybagQty === 'function') {
        syncFrostybagQty().then(function(cart) {
          if (cart) {
            CartDrawer.updateCount(getVisibleItemCount(cart));
            updateShippingProgress(cart);
          }
        });
      }
    },

    // bindConsent() removed 2026-09-04 — see comment in init(). If you need to
    // re-introduce a consent gate, don't disable the checkout button by default:
    // require the checkbox only for the FR/EU markets and add the UI back in
    // cart-drawer.liquid / cart-items.liquid.

    open() {
      this.el.classList.add('open');
      this.el.setAttribute('aria-hidden', 'false');
      if (this.overlay) this.overlay.classList.add('active');
      document.body.style.overflow = 'hidden';

      // Remember focus + move into drawer for a11y
      this._lastFocus = document.activeElement;
      requestAnimationFrame(() => {
        const closeBtn = document.getElementById('cart-drawer-close');
        if (closeBtn) closeBtn.focus();
      });

      // Refresh cart contents
      this.refresh();
    },

    close() {
      this.el.classList.remove('open');
      this.el.setAttribute('aria-hidden', 'true');
      if (this.overlay) this.overlay.classList.remove('active');
      document.body.style.overflow = '';
      // Restore focus to the trigger
      if (this._lastFocus && typeof this._lastFocus.focus === 'function') {
        this._lastFocus.focus();
      }
    },

    async refresh() {
      try {
        // Always sync frostybag before rendering so the drawer reflects the
        // correct auto-gift quantity.
        const synced = await (typeof syncFrostybagQty === 'function' ? syncFrostybagQty() : Promise.resolve(null));
        const cart = synced || await (await fetch(SIDERA_URL('cart.js'))).json();
        this.updateCount(getVisibleItemCount(cart));
        this.renderItems(cart);
        updateShippingProgress(cart);
      } catch (err) {
        console.warn('Cart refresh failed:', err);
      }
    },

    renderItems(cart) {
      if (!this.body) return;

      const footer = this.el.querySelector('.cart-drawer__footer');
      const giftNote = this.el.querySelector('.cart-drawer__gift-note');

      if (cart.item_count === 0) {
        const emptyText = this.el.dataset.emptyText || 'Your cart is empty';
        const emptyCta = this.el.dataset.emptyCta || 'SHOP NOW';
        const emptyCtaUrl = this.el.dataset.emptyCtaUrl || '/collections/all';
        this.body.innerHTML = `
          <div class="cart-drawer__empty">
            <p>${emptyText}</p>
            <a href="${emptyCtaUrl}" class="btn btn--primary">${emptyCta}</a>
          </div>`;
        if (footer) footer.style.display = 'none';
        if (giftNote) giftNote.style.display = 'none';
        return;
      }

      // Localized labels from data attributes
      const qtyDecreaseLabel = this.el.dataset.qtyDecrease || 'Decrease quantity';
      const qtyIncreaseLabel = this.el.dataset.qtyIncrease || 'Increase quantity';
      const removeLabel = this.el.dataset.removeLabel || 'Remove';

      if (footer) footer.style.display = '';

      // Gift-note element is a retired placeholder (Aline 24/07/26 removed its
      // content but kept the <div> for future JS hooks). Force-hide it so the
      // empty container doesn't render as a mystery blank band above SUBTOTAL.
      // If a gift note is re-introduced, restore the hasEligible logic here.
      if (giftNote) giftNote.style.display = 'none';

      // Update subtotal
      const subtotalEl = document.getElementById('cart-subtotal');
      if (subtotalEl) {
        subtotalEl.textContent = this.formatMoney(cart.total_price);
      }

      const esc = (s) => String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

      // Render line-item properties (e.g. Free Astrology Medallion: Leo)
      const renderProps = (item) => {
        if (!item.properties) return '';
        const props = Array.isArray(item.properties)
          ? item.properties
          : Object.entries(item.properties).map(([k, v]) => [k, v]);
        const lines = props
          .filter(([k, v]) => v && !String(k).startsWith('_'))
          .map(([k, v]) => `<p class="cart-item__property"><span class="cart-item__property-label">${esc(k)}:</span> ${esc(v)}</p>`);
        return lines.join('');
      };

      // Aline 24/07/26: hide FROSTYBAG-M from client cart view entirely (was previously rendered as "HOUSSE DE PROTECTION" gift line).
      // The item stays in the Shopify cart for fulfillment — we just don't render it in the drawer.
      var BAG_M = window.SIDERA_FROSTYBAG_M_VARIANT_ID || window.SIDERA_FROSTYBAG_VARIANT_ID;
      var BAG_S = window.SIDERA_FROSTYBAG_S_VARIANT_ID;

      // Render items — skip both auto-gift bag rows (M + S)
      const itemsHtml = cart.items.filter(item => item.variant_id !== BAG_M && item.variant_id !== BAG_S).map(item => {
        return `
        <li class="cart-item" id="cart-item-${esc(item.key).replace(/[/:]/g, '-')}">
          <a href="${esc(item.url)}" class="cart-item__image-link">
            ${item.image ? `<img class="cart-item__image" src="${esc(item.image.replace('http:', 'https:'))}" alt="${esc(item.title)}" loading="lazy" width="80" height="104">` : ''}
          </a>
          <div class="cart-item__details">
            <a href="${esc(item.url)}" class="cart-item__name">${esc(item.product_title)}</a>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${esc(item.variant_title)}</p>` : ''}
            ${renderProps(item)}
            <p class="cart-item__price">${this.formatMoney(item.final_line_price)}</p>
            <div class="cart-item__quantity">
              <button class="qty-btn" data-key="${esc(item.key)}" data-change="-1" aria-label="${esc(qtyDecreaseLabel)}">−</button>
              <span class="qty-value">${item.quantity}</span>
              <button class="qty-btn" data-key="${esc(item.key)}" data-change="1" aria-label="${esc(qtyIncreaseLabel)}">+</button>
            </div>
          </div>
          <button class="cart-item__remove" data-key="${esc(item.key)}" aria-label="${esc(removeLabel)} ${esc(item.product_title)}">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </button>
        </li>`;
      }).join('');

      this.body.innerHTML = `<ul class="cart-items" role="list">${itemsHtml}</ul>`;
    },

    async handleQtyChange(btn) {
      const key = btn.dataset.key;
      const change = parseInt(btn.dataset.change, 10);
      const item = this.body.querySelector(`#cart-item-${key.replace(/[/:]/g, '-')}`);
      if (!item) return;

      const qtyEl = item.querySelector('.qty-value');
      const currentQty = parseInt(qtyEl.textContent, 10);
      const newQty = Math.max(0, currentQty + change);

      try {
        await fetch(SIDERA_URL('cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: newQty })
        });
        // Re-sync frostybag qty then re-render.
        const cart = (typeof syncFrostybagQty === 'function' ? await syncFrostybagQty() : null)
          || await (await fetch(SIDERA_URL('cart.js'))).json();
        this.updateCount(getVisibleItemCount(cart));
        this.renderItems(cart);
        updateShippingProgress(cart);
      } catch (err) {
        console.warn('Cart update failed:', err);
      }
    },

    async handleRemove(btn) {
      const key = btn.dataset.key;
      try {
        await fetch(SIDERA_URL('cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        const cart = (typeof syncFrostybagQty === 'function' ? await syncFrostybagQty() : null)
          || await (await fetch(SIDERA_URL('cart.js'))).json();
        this.updateCount(getVisibleItemCount(cart));
        this.renderItems(cart);
        updateShippingProgress(cart);
      } catch (err) {
        console.warn('Cart remove failed:', err);
      }
    },

    async quickAdd(btn) {
      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      btn.disabled = true;
      btn.textContent = '...';

      try {
        const res = await fetch(SIDERA_URL('cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });
        const data = await res.json();
        if (!res.ok) {
          const msg = (data && (data.description || data.message)) || SIDERA_T('cart_add_error');
          alert(msg);
          btn.textContent = SIDERA_T('quick_add');
          btn.disabled = false;
          console.error('Quick add rejected:', data);
          return;
        }
        btn.textContent = SIDERA_T('added');
        setTimeout(() => {
          btn.textContent = SIDERA_T('quick_add');
          btn.disabled = false;
        }, 1500);
        this.open();
      } catch (err) {
        btn.textContent = SIDERA_T('quick_add');
        btn.disabled = false;
        console.warn('Quick add failed:', err);
      }
    },

    async addToCart(form) {
      const submitBtn = form.querySelector('[name="add"]');
      if (!submitBtn) return;
      const originalText = submitBtn.textContent;

      // Check for the free astrology medallion selector
      const medallionSelect = form.querySelector('#medallion-select');
      const medallionError = document.getElementById('medallion-error');

      if (medallionSelect) {
        if (!medallionSelect.value) {
          if (medallionError) medallionError.hidden = false;
          medallionSelect.focus();
          medallionSelect.classList.add('medallion-gift__select--error');
          return;
        }
        if (medallionError) medallionError.hidden = true;
        medallionSelect.classList.remove('medallion-gift__select--error');
      }

      submitBtn.disabled = true;
      submitBtn.textContent = '...';

      try {
        let body, headers;

        if (medallionSelect && medallionSelect.value) {
          // Add main product + free medallion as one cart call
          const mainVariantId = parseInt(form.querySelector('input[name="id"]').value, 10);
          const giftVariantId = parseInt(medallionSelect.value, 10);
          body = JSON.stringify({
            items: [
              { id: mainVariantId, quantity: 1 },
              { id: giftVariantId, quantity: 1, properties: { '_gift_with_purchase': 'true' } }
            ]
          });
          headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
        } else {
          body = new FormData(form);
          headers = { 'Accept': 'application/json' };
        }

        const res = await fetch(SIDERA_URL('cart/add.js'), { method: 'POST', headers, body });
        const data = await res.json();
        if (!res.ok) {
          const msg = (data && (data.description || data.message)) || 'Sorry, we could not add this item to your cart.';
          if (medallionError) {
            medallionError.textContent = msg;
            medallionError.hidden = false;
          } else {
            alert(msg);
          }
          console.error('Add to cart rejected:', data);
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
          return;
        }
        submitBtn.textContent = SIDERA_T('added');
        setTimeout(() => {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }, 1500);

        // Meta Pixel: AddToCart event (only fires if consent granted and pixel loaded)
        try {
          if (window.fbq && window.__metaPixelLoaded) {
            const items = Array.isArray(data.items) ? data.items : (data.id ? [data] : []);
            const added = items[0] || data;
            fbq('track', 'AddToCart', {
              content_ids: [String(added.variant_id || added.id)],
              content_name: added.product_title || added.title,
              content_type: 'product',
              value: (added.final_line_price || added.line_price || added.price || 0) / 100,
              currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR'
            });
          }
        } catch (e) { /* silent */ }

        this.open();
      } catch (err) {
        submitBtn.textContent = originalText;
        submitBtn.disabled = false;
        console.warn('Add to cart failed:', err);
      }
    },

    updateCount(count) {
      const countEl = document.getElementById('cart-count');
      if (countEl) {
        countEl.textContent = count > 0 ? count : '';
        countEl.style.display = count > 0 ? '' : 'none';
      }
    },

    formatMoney(cents) {
      const amount = (cents || 0) / 100;
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
      // Use the browser's Intl formatter so we get the proper currency symbol
      // ($, €, £, CHF, etc.) instead of the raw ISO code text.
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: currency,
          currencyDisplay: 'narrowSymbol'
        }).format(amount);
      } catch (e) {
        return `${currency} ${amount.toFixed(2)}`;
      }
    }
  };

  // ============================================================
  // 5. PRODUCT FILTER TABS
  // ============================================================
  const FilterTabs = {
    init() {
      const tabs = document.querySelectorAll('.filter-tab');
      const grid = document.getElementById('product-grid');
      if (!tabs.length || !grid) return;

      // Tabs are now real <a> links to /collections/{handle}. Skip client-side filter
      // when data-filter is not set (which is the current default). Kept for backward
      // compatibility with any preset that still ships button-style tabs.
      tabs.forEach(tab => {
        if (!tab.dataset.filter) return;
        tab.addEventListener('click', () => {
          const filter = tab.dataset.filter;
          tabs.forEach(t => {
            t.classList.remove('active');
            t.setAttribute('aria-selected', 'false');
          });
          tab.classList.add('active');
          tab.setAttribute('aria-selected', 'true');
          const cards = grid.querySelectorAll('.product-card-wrap');
          cards.forEach(card => {
            if (filter === 'all') {
              card.classList.remove('hidden');
            } else {
              const tags = card.dataset.tags || '';
              const matches = tags.includes(filter.toLowerCase());
              card.classList.toggle('hidden', !matches);
            }
          });
        });
      });
    }
  };

  // ============================================================
  // 6. PRODUCT GALLERY
  // ============================================================
  // ============================================================
  // 6b. BACK IN STOCK — Klaviyo integration
  // ============================================================
  //
  // Requires the Klaviyo public API key (also called Site ID / Company ID),
  // a ~6-character alphanumeric code from Klaviyo > Account > Settings >
  // API Keys > Public API Key. Set it in window.SIDERA_KLAVIYO_KEY via a
  // small snippet in layout/theme.liquid, or hardcode below.
  //
  const KLAVIYO_PUBLIC_KEY = (window.SIDERA_KLAVIYO_KEY || 'U4v4zM');

  const BackInStock = {
    init() {
      const openBtn = document.querySelector('[data-bis-open]');
      const form = document.querySelector('[data-bis-form]');
      if (!openBtn || !form) return;

      const submitBtn = form.querySelector('[data-bis-submit]');
      const emailInput = form.querySelector('[data-bis-email]');
      const variantInput = form.querySelector('[data-bis-variant-id]');
      const successEl = form.querySelector('[data-bis-success]');
      const errorEl = form.querySelector('[data-bis-error]');

      openBtn.addEventListener('click', () => {
        form.hidden = false;
        if (successEl) successEl.hidden = true;
        if (errorEl) errorEl.hidden = true;
        setTimeout(() => emailInput && emailInput.focus(), 50);
      });

      // Enter key in input triggers submit
      if (emailInput) {
        emailInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submitBtn && submitBtn.click();
          }
        });
      }

      if (submitBtn) {
        submitBtn.addEventListener('click', async () => {
          const email = (emailInput && emailInput.value || '').trim();
          const variantId = variantInput && variantInput.value;

          // Simple client-side email validation
          const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
          if (!emailOk || !variantId) {
            if (errorEl) errorEl.hidden = false;
            if (successEl) successEl.hidden = true;
            return;
          }

          submitBtn.disabled = true;
          if (errorEl) errorEl.hidden = true;

          try {
            const body = new URLSearchParams();
            body.append('a', KLAVIYO_PUBLIC_KEY);
            body.append('email', email);
            body.append('variant', variantId);
            body.append('product', variantId);
            body.append('platform', 'shopify');
            body.append('subscriptions', JSON.stringify([
              { type: 'back_in_stock', channels: { email: ['EMAIL'] } }
            ]));

            const resp = await fetch(
              'https://a.klaviyo.com/onsite/components/back-in-stock/subscribe',
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: body.toString(),
              }
            );

            if (resp.ok) {
              if (successEl) successEl.hidden = false;
              if (emailInput) emailInput.value = '';
            } else {
              if (errorEl) errorEl.hidden = false;
            }
          } catch (err) {
            if (errorEl) errorEl.hidden = false;
          } finally {
            submitBtn.disabled = false;
          }
        });
      }
    }
  };

  const ProductGallery = {
    init() {
      const gallery = document.querySelector('[data-product-gallery]');
      if (!gallery) return;
      const track = gallery.querySelector('[data-gallery-track]');
      const slides = gallery.querySelectorAll('.product-gallery__slide');
      if (!track || !slides.length) return;

      const counterEl = gallery.querySelector('[data-gallery-counter-current]');
      const dots = gallery.querySelectorAll('[data-gallery-dot]');
      const prevBtn = gallery.querySelector('[data-gallery-prev]');
      const nextBtn = gallery.querySelector('[data-gallery-next]');
      const total = slides.length;
      const isDesktop = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      // Per-slide hover-zoom (desktop only) — works fine inside the carousel:
      // the active slide fills the track viewport, so mousemove coords map to
      // the visible image just like before.
      slides.forEach((slide) => {
        const lens = slide.querySelector('.product-gallery__zoom-lens');
        if (!lens || !isDesktop) return;

        const applyBg = () => {
          const url = slide.dataset.zoomSrc;
          if (url) lens.style.backgroundImage = `url("${url}")`;
        };

        slide.addEventListener('mouseenter', () => {
          applyBg();
          slide.classList.add('is-zooming');
        });
        slide.addEventListener('mousemove', (e) => {
          const rect = slide.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          lens.style.backgroundPosition = `${x}% ${y}%`;
        });
        slide.addEventListener('mouseleave', () => {
          slide.classList.remove('is-zooming');
        });
      });

      // Compute current slide from horizontal scroll position and update UI.
      const updateActive = () => {
        const trackWidth = track.clientWidth || 1;
        const idx = Math.round(track.scrollLeft / trackWidth); // 0-based
        const oneBased = idx + 1;
        if (counterEl) counterEl.textContent = String(oneBased);
        dots.forEach((dot) => {
          const active = Number(dot.dataset.galleryDot) === oneBased;
          dot.classList.toggle('is-active', active);
          if (active) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
        // Disable arrows at extremes (no wrap-around — matches Revolve behaviour)
        if (prevBtn) prevBtn.disabled = idx <= 0;
        if (nextBtn) nextBtn.disabled = idx >= total - 1;
      };

      const goToIndex = (zeroIdx) => {
        const clamped = Math.max(0, Math.min(total - 1, zeroIdx));
        const target = slides[clamped];
        if (target) track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      };

      if (prevBtn) prevBtn.addEventListener('click', () => {
        const trackWidth = track.clientWidth || 1;
        const currentIdx = Math.round(track.scrollLeft / trackWidth);
        goToIndex(currentIdx - 1);
      });
      if (nextBtn) nextBtn.addEventListener('click', () => {
        const trackWidth = track.clientWidth || 1;
        const currentIdx = Math.round(track.scrollLeft / trackWidth);
        goToIndex(currentIdx + 1);
      });

      // rAF-throttled scroll listener for smooth counter/dot updates.
      let scrollTicking = false;
      track.addEventListener('scroll', () => {
        if (scrollTicking) return;
        scrollTicking = true;
        requestAnimationFrame(() => {
          updateActive();
          scrollTicking = false;
        });
      }, { passive: true });

      // Click on a dot → smooth scroll the track to that slide.
      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const oneBased = Number(dot.dataset.galleryDot);
          if (!oneBased) return;
          const target = slides[oneBased - 1];
          if (!target) return;
          track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
        });
      });

      // Keep the active dot right if the layout resizes (e.g. rotate on mobile).
      window.addEventListener('resize', () => {
        // No smooth: snap to the current slide's new offsetLeft.
        const currentDot = gallery.querySelector('.product-gallery__dot.is-active');
        const oneBased = currentDot ? Number(currentDot.dataset.galleryDot) : 1;
        const target = slides[oneBased - 1];
        if (target) track.scrollLeft = target.offsetLeft;
      });

      // Bidirectional sync: variant-linked slides sync form on click
      slides.forEach((slide) => {
        const variantId = slide.dataset.variantId;
        const variantTitle = slide.dataset.variantTitle;
        if (!variantId) return;
        // Sync selection when the slide caption is tapped (not the image itself,
        // to avoid conflict with zoom). Click on caption pill = select variant.
        const caption = slide.querySelector('.product-gallery__slide-caption');
        if (!caption) return;
        caption.style.cursor = 'pointer';
        caption.style.pointerEvents = 'auto';
        caption.addEventListener('click', (e) => {
          e.stopPropagation();
          const form = document.querySelector('form[action*="/cart/add"]');
          if (!form) return;
          if (variantTitle) {
            const parts = variantTitle.split(' / ');
            parts.forEach((part) => {
              const radio = form.querySelector('input[type="radio"][value="' + CSS.escape(part) + '"]');
              if (radio && !radio.checked) {
                radio.checked = true;
                radio.dispatchEvent(new Event('change', { bubbles: true }));
              }
            });
          }
          const idInput = form.querySelector('input[name="id"]');
          if (idInput && idInput.value !== variantId) idInput.value = variantId;
        });
      });

      // Initial sync
      updateActive();
    },

    /**
     * Bring the slide matching the given variant into view inside the carousel.
     * Used by VariantSelector when the user picks a size/color with its own image.
     */
    scrollToVariantSlide(variant) {
      if (!variant) return;
      const gallery = document.querySelector('[data-product-gallery]');
      if (!gallery) return;
      const track = gallery.querySelector('[data-gallery-track]');
      if (!track) return;

      const variantId = String(variant.id || '');
      const imageId = variant.featured_image ? String(variant.featured_image.id || '') : '';

      let target = null;
      const slides = gallery.querySelectorAll('.product-gallery__slide');
      slides.forEach((slide) => {
        if (target) return;
        if (variantId && slide.dataset.variantId === variantId) { target = slide; return; }
        if (imageId && slide.dataset.imageId === imageId) { target = slide; }
      });
      if (!target) return;

      // Scroll the horizontal track to that slide.
      track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    }
  };

  // ============================================================
  // 7. VARIANT SELECTOR (Product Page)
  // ============================================================
  const VariantSelector = {
    init() {
      const form = document.getElementById('product-form');
      if (!form) return;

      const radios = form.querySelectorAll('input[type="radio"]');
      radios.forEach(radio => {
        radio.addEventListener('change', () => {
          this.updateOptions(form);
        });
      });
    },

    updateOptions(form) {
      // Visual state update
      const allLabels = form.querySelectorAll('.option-value');
      allLabels.forEach(label => {
        const input = label.querySelector('input');
        if (input) label.classList.toggle('selected', input.checked);
      });

      // Get selected option values
      const selected = [];
      form.querySelectorAll('input[type="radio"]:checked').forEach(input => {
        selected.push(input.value);
      });

      // Find matching variant from JSON
      const variantsEl = document.getElementById('product-variants-json');
      if (!variantsEl) return;

      try {
        const variants = JSON.parse(variantsEl.textContent);
        const match = variants.find(v => {
          return v.options.every((opt, i) => opt === selected[i]);
        });

        if (match) {
          // Update hidden variant ID input
          const idInput = form.querySelector('input[name="id"]');
          if (idInput) idInput.value = match.id;

          // Update Add to Cart / Back-in-stock button visibility
          const addBtn = form.querySelector('[data-add-btn]');
          const bisBtn = form.querySelector('[data-bis-open]');
          const bisForm = document.querySelector('[data-bis-form]');
          const bisVariantInput = document.querySelector('[data-bis-variant-id]');
          if (addBtn && bisBtn) {
            if (match.available) {
              addBtn.hidden = false;
              addBtn.disabled = false;
              addBtn.textContent = SIDERA_T('add_to_cart');
              bisBtn.hidden = true;
              if (bisForm) bisForm.hidden = true;
            } else {
              addBtn.hidden = true;
              bisBtn.hidden = false;
              if (bisForm) {
                // Reset form state when variant changes
                bisForm.hidden = true;
                const successEl = bisForm.querySelector('[data-bis-success]');
                const errorEl = bisForm.querySelector('[data-bis-error]');
                if (successEl) successEl.hidden = true;
                if (errorEl) errorEl.hidden = true;
              }
            }
          }
          if (bisVariantInput) bisVariantInput.value = match.id;

          // Update price
          const priceEl = document.querySelector('#product-price');
          if (priceEl && match.price) {
            const formatted = (match.price / 100).toLocaleString('fr-FR', {style: 'currency', currency: 'EUR'});
            priceEl.textContent = formatted;
          }

          // Update SKU to match the selected variant
          const skuValueEl = document.getElementById('product-sku-value');
          if (skuValueEl) {
            skuValueEl.textContent = match.sku || '';
            const skuWrapper = document.getElementById('product-sku');
            if (skuWrapper) skuWrapper.hidden = !match.sku;
          }

          // Stacked gallery: instead of swapping a single main image, scroll
          // the page to the slide that matches this variant's featured image.
          if (match.featured_image && typeof ProductGallery !== 'undefined' && ProductGallery.scrollToVariantSlide) {
            ProductGallery.scrollToVariantSlide(match);
          }
        }
      } catch(e) {
        console.error('Variant update error:', e);
      }
    }
  };

  // ============================================================
  // 8. SMOOTH SCROLL
  // ============================================================
  const SmoothScroll = {
    init() {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', (e) => {
          const href = anchor.getAttribute('href');
          if (href === '#') return;
          const target = document.querySelector(href);
          if (target) {
            e.preventDefault();
            const headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--nav-height'), 10) || 64;
            const top = target.getBoundingClientRect().top + window.scrollY - headerH;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        });
      });
    }
  };

  // ============================================================
  // 9. LAZY LOADING (native + intersection observer fallback)
  // ============================================================
  const LazyLoad = {
    init() {
      // Native lazy loading is set in HTML (loading="lazy")
      // IntersectionObserver for JS-driven reveals
      if (!('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

      document.querySelectorAll('.zodiac-card, .evening-edit__item, .product-card').forEach(el => {
        observer.observe(el);
      });
    }
  };

  // ============================================================
  // 10. SCROLL REVEAL ANIMATIONS
  // ============================================================
  const ScrollReveal = {
    init() {
      if (!('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.05, rootMargin: '0px 0px -80px 0px' });

      const selectors = [
        '.section-label',
        '.hero-content',
        '.evening-edit__headline',
        '.brand-story__content',
        '.pull-quote__text',
        '.newsletter__inner',
        '.zodiac-hero__inner',
        '.contact-form-section__header'
      ];

      document.querySelectorAll(selectors.join(', ')).forEach(el => {
        el.style.opacity = '0';
        el.style.transform = 'translateY(20px)';
        el.style.transition = 'opacity 0.7s ease, transform 0.7s ease';
        observer.observe(el);
      });
    }
  };

  // ============================================================
  // STAR CANVAS — twinkling starfield (matches prototype exactly)
  // ============================================================
  const StarCanvas = {
    animId: null,
    resizeHandler: null,
    init() {
      const canvas = document.getElementById('star-canvas');
      if (!canvas) return;

      // Cancel previous loop / listener if re-initialized (theme editor)
      if (this.animId) cancelAnimationFrame(this.animId);
      if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);

      const ctx = canvas.getContext('2d');
      let stars = [];

      const initStars = () => {
        const count = Math.floor((canvas.width * canvas.height) / 8000);
        stars = Array.from({ length: count }, () => ({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          r: Math.random() * 1.2 + 0.2,
          a: Math.random(),
          speed: Math.random() * 0.005 + 0.002,
          phase: Math.random() * Math.PI * 2
        }));
      };

      const resize = () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        initStars();
      };
      this.resizeHandler = resize;

      const self = this;
      const draw = (t) => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const isDark = document.documentElement.classList.contains('dark');
        stars.forEach(s => {
          const alpha = s.a * (0.5 + 0.5 * Math.sin(t * s.speed + s.phase));
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
          ctx.fillStyle = isDark
            ? `rgba(251, 251, 243, ${alpha * 0.6})`
            : `rgba(26, 24, 20, ${alpha * 0.15})`;
          ctx.fill();
        });
        self.animId = requestAnimationFrame(draw);
      };

      window.addEventListener('resize', resize, { passive: true });
      resize();
      self.animId = requestAnimationFrame(draw);
    }
  };

  // ============================================================
  // INIT
  // ============================================================
  function init() {
    document.documentElement.classList.remove('no-js');

    DarkMode.init();
    StarCanvas.init();
    Header.init();
    HeaderSearch.init();
    MobileMenu.init();
    MobileDrawer.init();
    CartDrawer.init();
    FilterTabs.init();
    ProductGallery.init();
    VariantSelector.init();
    BackInStock.init();
    SmoothScroll.init();
    LazyLoad.init();

    // Add CSS for revealed state (idempotent)
    if (!document.getElementById('sidera-reveal-style')) {
      const style = document.createElement('style');
      style.id = 'sidera-reveal-style';
      style.textContent = '.revealed { opacity: 1 !important; transform: translateY(0) !important; }';
      document.head.appendChild(style);
    }

    // Trigger scroll reveal after a tick (so initial positions are set)
    requestAnimationFrame(() => ScrollReveal.init());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

/* ============================================
   Size Guide Modal
   ============================================ */
(function() {
  'use strict';

  function initSizeGuide() {
    var modal = document.getElementById('size-guide-modal');
    if (!modal) return;

    var openers = document.querySelectorAll('[data-size-guide-open]');
    if (!openers.length) return;

    var lastFocused = null;
    var savedScrollY = 0;
    var isOpen = false;
    var historyPushed = false;

    function lockScroll() {
      savedScrollY = window.scrollY || window.pageYOffset || 0;
      // iOS-safe scroll lock: pin body in place at negative top so the visible
      // viewport doesn't jump. Preserves layout width.
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + savedScrollY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.classList.add('sg-modal-open');
    }

    function unlockScroll() {
      var y = savedScrollY;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.classList.remove('sg-modal-open');
      // Restore exact scroll position (bypass smooth scroll)
      var prevBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(0, y);
      document.documentElement.style.scrollBehavior = prevBehavior;
    }

    var origScrollRestoration = null;

    function open() {
      if (isOpen) return;
      isOpen = true;
      lastFocused = document.activeElement;

      // Take manual control of scroll restoration so the browser doesn't
      // fight us on Back-button restore.
      try {
        if ('scrollRestoration' in window.history) {
          origScrollRestoration = window.history.scrollRestoration;
          window.history.scrollRestoration = 'manual';
        }
      } catch (e) { /* noop */ }

      // Push history entry BEFORE locking scroll so it's a lightweight sentinel.
      try {
        if (window.history && typeof window.history.pushState === 'function') {
          window.history.pushState({ sizeGuideOpen: true }, '');
          historyPushed = true;
        }
      } catch (e) {
        historyPushed = false;
      }

      modal.hidden = false;
      lockScroll();
      // Force reflow so the transition fires
      // eslint-disable-next-line no-unused-expressions
      modal.offsetHeight;
      modal.setAttribute('data-open', 'true');

      var closeBtn = modal.querySelector('.sg-modal__close');
      if (closeBtn) closeBtn.focus();
    }

    // Perform the actual DOM/scroll close work. Called only from popstate
    // so there's a single code path for both button-click and browser-Back.
    function _doClose() {
      if (!isOpen) return;
      isOpen = false;
      modal.setAttribute('data-open', 'false');
      unlockScroll();
      setTimeout(function() { modal.hidden = true; }, 320);
      if (lastFocused && typeof lastFocused.focus === 'function') {
        try { lastFocused.focus({ preventScroll: true }); }
        catch (e) { lastFocused.focus(); }
      }
      // Restore original scrollRestoration mode
      try {
        if (origScrollRestoration !== null && 'scrollRestoration' in window.history) {
          window.history.scrollRestoration = origScrollRestoration;
          origScrollRestoration = null;
        }
      } catch (e) { /* noop */ }
      historyPushed = false;
    }

    // Public close: triggered by the Fermer buttons and Escape key.
    // If we pushed a history entry, use history.back() which fires popstate
    // (single code path). Otherwise close directly.
    function close() {
      if (!isOpen) return;
      if (historyPushed) {
        // Popstate handler will call _doClose
        try { window.history.back(); return; }
        catch (e) { /* fall through */ }
      }
      _doClose();
    }

    openers.forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        open();
      });
    });

    modal.querySelectorAll('[data-size-guide-close]').forEach(function(el) {
      el.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        close();
      });
    });

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && isOpen) {
        close();
      }
    });

    // Browser Back button (mobile & desktop) closes the modal instead of
    // navigating away from the product page. Single close path.
    window.addEventListener('popstate', function() {
      if (isOpen) {
        historyPushed = false; // history already popped by browser
        _doClose();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSizeGuide);
  } else {
    initSizeGuide();
  }
})();

/* ============================================
   Newsletter Popup — lead capture with 10% welcome
   ============================================ */
(function() {
  'use strict';

  var STORAGE_KEY_DISMISSED = 'sidera_np_dismissed_at';
  var STORAGE_KEY_SUBSCRIBED = 'sidera_np_subscribed_at';
  // Session-seen flag is namespaced by locale so switching FR ↔ EN reopens
  // the popup on the new-language page (each locale gets its own version).
  var NP_LOCALE = (document.documentElement.lang || 'fr').toLowerCase().indexOf('en') === 0 ? 'en' : 'fr';
  var STORAGE_KEY_SEEN_SESSION = 'sidera_np_seen_session_' + NP_LOCALE;
  var DISMISS_TTL_DAYS = 3;       // Re-show trigger 3 days after dismiss
  var SUBSCRIBED_TTL_DAYS = 365;  // Never show again for 1 year after subscribe

  function initNewsletterPopup() {
    var wrapper = document.getElementById('newsletter-popup');
    if (!wrapper) return;

    var isPreview = wrapper.getAttribute('data-preview') === 'true';
    var forceOpen = wrapper.getAttribute('data-force-open') === 'true';
    var autoDelay = parseInt(wrapper.getAttribute('data-auto-delay-ms') || '20000', 10);

    var modal = wrapper.querySelector('.np-modal');
    var trigger = wrapper.querySelector('.np-trigger');
    var form = wrapper.querySelector('#np-form');
    var input = wrapper.querySelector('#np-email');
    var submitBtn = wrapper.querySelector('.np-form__submit');
    var msg = wrapper.querySelector('[data-np-msg]');
    var successPane = wrapper.querySelector('[data-np-success]');

    if (!modal || !trigger) return;

    // Check dismissal/subscription state (skipped in preview mode)
    function shouldShow() {
      if (isPreview) return true;
      try {
        var subscribed = localStorage.getItem(STORAGE_KEY_SUBSCRIBED);
        if (subscribed) {
          var age = (Date.now() - parseInt(subscribed, 10)) / 86400000;
          if (age < SUBSCRIBED_TTL_DAYS) return false;
        }
        var dismissed = localStorage.getItem(STORAGE_KEY_DISMISSED);
        if (dismissed) {
          var dage = (Date.now() - parseInt(dismissed, 10)) / 86400000;
          if (dage < DISMISS_TTL_DAYS) return false;
        }
      } catch (e) { /* localStorage disabled — still show */ }
      return true;
    }

    if (!shouldShow()) return;

    // Reveal wrapper container (needed for the modal to be interactable).
    // The sticky trigger pill stays hidden by default — the popup rises from
    // the bottom automatically after consent, without a preceding trigger.
    // If the user closes the popup without subscribing, we then reveal the
    // pill as a discreet reminder to come back.
    wrapper.hidden = false;

    // Modal open/close logic
    var isOpen = false;
    var savedScrollY = 0;
    var lastFocused = null;
    var historyPushed = false;
    var origScrollRestoration = null;

    // The popup does NOT lock body scroll — the page underneath stays
    // scrollable behind it (editorial ETERNE-style behaviour). We only
    // add a marker class for CSS hooks (e.g. defensive pill hide).
    function lockScroll() {
      document.body.classList.add('np-modal-open');
    }

    function unlockScroll() {
      document.body.classList.remove('np-modal-open');
    }

    function openModal() {
      if (isOpen) return;
      isOpen = true;
      lastFocused = document.activeElement;

      try {
        if ('scrollRestoration' in window.history) {
          origScrollRestoration = window.history.scrollRestoration;
          window.history.scrollRestoration = 'manual';
        }
      } catch (e) { /* noop */ }

      try {
        if (window.history && typeof window.history.pushState === 'function') {
          window.history.pushState({ npOpen: true }, '');
          historyPushed = true;
        }
      } catch (e) { historyPushed = false; }

      modal.hidden = false;
      lockScroll();
      // eslint-disable-next-line no-unused-expressions
      modal.offsetHeight;
      modal.setAttribute('data-open', 'true');
      trigger.setAttribute('aria-expanded', 'true');

      setTimeout(function() {
        try { input.focus({ preventScroll: true }); }
        catch (e) { input.focus(); }
      }, 400);
    }

    function _doClose() {
      if (!isOpen) return;
      isOpen = false;
      modal.setAttribute('data-open', 'false');
      trigger.setAttribute('aria-expanded', 'false');
      unlockScroll();
      // Show sticky reminder pill after the modal is closed without a
      // successful subscription (preview mode also reveals it so Kate/Ygal
      // can see both surfaces).
      var subscribed = false;
      try { subscribed = !!localStorage.getItem(STORAGE_KEY_SUBSCRIBED); }
      catch (e) { /* noop */ }
      if (!subscribed) {
        wrapper.classList.add('np--visible');
      }
      setTimeout(function() { modal.hidden = true; }, 320);
      if (lastFocused && typeof lastFocused.focus === 'function') {
        try { lastFocused.focus({ preventScroll: true }); }
        catch (e) { lastFocused.focus(); }
      }
      try {
        if (origScrollRestoration !== null && 'scrollRestoration' in window.history) {
          window.history.scrollRestoration = origScrollRestoration;
          origScrollRestoration = null;
        }
      } catch (e) { /* noop */ }
      historyPushed = false;
    }

    function closeModal() {
      if (!isOpen) return;
      if (historyPushed) {
        try { window.history.back(); return; }
        catch (e) { /* fallthrough */ }
      }
      _doClose();
    }

    function dismissTrigger() {
      wrapper.classList.remove('np--visible');
      try { localStorage.setItem(STORAGE_KEY_DISMISSED, String(Date.now())); }
      catch (e) { /* noop */ }
    }

    // Wire triggers
    wrapper.querySelectorAll('[data-np-open]').forEach(function(el) {
      el.addEventListener('click', function(e) {
        // Don't open when the inner close (X) inside the trigger is clicked
        if (e.target && e.target.closest('[data-np-dismiss]')) return;
        e.preventDefault();
        openModal();
      });
    });

    wrapper.querySelectorAll('[data-np-close]').forEach(function(el) {
      el.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        closeModal();
      });
    });

    wrapper.querySelectorAll('[data-np-dismiss]').forEach(function(el) {
      el.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        dismissTrigger();
      });
      el.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          dismissTrigger();
        }
      });
    });

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && isOpen) closeModal();
    });

    window.addEventListener('popstate', function() {
      if (isOpen) {
        historyPushed = false;
        _doClose();
      }
    });

    // Auto-open flow — the popup rises from the bottom automatically
    // AFTER the visitor interacts with the GDPR cookie banner (any choice
    // is fine, they don't need to grant marketing cookies to opt-in via
    // email). Fallback: if no consent event fires within ~4s, we assume
    // the visitor already has a stored consent decision and open anyway.

    var openedAuto = false;
    function autoOpenOnce() {
      if (openedAuto || isOpen) return;
      openedAuto = true;
      try { sessionStorage.setItem(STORAGE_KEY_SEEN_SESSION, '1'); }
      catch (e) { /* noop */ }
      // Small delay so the popup doesn't collide visually with the banner
      // closing animation.
      setTimeout(openModal, 600);
    }

    var seenSession = false;
    try { seenSession = sessionStorage.getItem(STORAGE_KEY_SEEN_SESSION) === '1'; }
    catch (e) { /* noop */ }

    if (forceOpen) {
      // Preview mode: force open immediately, ignore session/consent state
      setTimeout(openModal, 300);
    } else if (!seenSession) {
      // Wait for the visitorConsentCollected event (fires after user
      // clicks accept/reject in the Shopify cookie banner). This is the
      // same signal GA4 + Meta Pixel already listen for in this theme.
      var consentHandler = function() {
        document.removeEventListener('visitorConsentCollected', consentHandler);
        autoOpenOnce();
      };
      document.addEventListener('visitorConsentCollected', consentHandler);

      // Fallback: if the banner never appears (returning visitor whose
      // consent is already stored) fire after ~4s so the popup still opens.
      setTimeout(function() {
        try {
          if (window.Shopify && window.Shopify.customerPrivacy) {
            // If customerPrivacy is initialised, the banner has resolved.
            document.removeEventListener('visitorConsentCollected', consentHandler);
            autoOpenOnce();
            return;
          }
        } catch (e) { /* noop */ }
        // Extra fallback — open anyway after the auto-delay window
        setTimeout(autoOpenOnce, Math.max(0, autoDelay - 4000));
      }, 4000);
    }

    // Form submission — POST to Klaviyo's legacy kmail-lists.com endpoint
    // using x-www-form-urlencoded so the browser treats this as a "simple"
    // CORS request (no preflight OPTIONS required). The modern client API
    // /client/subscriptions/ has a Cloudflare CORS bug where
    // Access-Control-Allow-Methods returns only OPTIONS, blocking POST on
    // strict browsers (Safari mobile in particular).
    var KLAVIYO_COMPANY_ID = 'U4v4zM';
    var KLAVIYO_NEWSLETTER_LIST_ID = 'UgNaEi';

    if (form) {
      form.addEventListener('submit', function(e) {
        e.preventDefault();
        var email = (input.value || '').trim();
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          msg.textContent = (document.documentElement.lang || '').indexOf('en') === 0
            ? 'Please enter a valid email address.'
            : 'Merci d\'entrer un email valide.';
          msg.setAttribute('data-state', 'error');
          input.focus();
          return;
        }

        submitBtn.disabled = true;
        msg.textContent = '';
        msg.removeAttribute('data-state');

        var body = new URLSearchParams();
        body.append('g', KLAVIYO_NEWSLETTER_LIST_ID);
        body.append('email', email);
        body.append('$fields', 'email');
        body.append('$source', 'popup-first-purchase-discount');
        body.append('company_id', KLAVIYO_COMPANY_ID);

        fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString()
        }).then(function(resp) {
          if (!resp.ok) {
            onError((document.documentElement.lang || '').indexOf('en') === 0
              ? 'Something went wrong. Please try again.'
              : 'Une erreur est survenue. Merci de réessayer.');
            return;
          }
          return resp.json().then(function(data) {
            // Klaviyo legacy endpoint returns { success: true, errors: [] } on OK.
            // Also treat any 200 with no explicit failure as success (dedupe cases).
            if (data && data.success !== false) {
              onSuccess();
            } else {
              onError((document.documentElement.lang || '').indexOf('en') === 0
                ? 'Something went wrong. Please try again.'
                : 'Une erreur est survenue. Merci de réessayer.');
            }
          });
        }).catch(function() {
          onError((document.documentElement.lang || '').indexOf('en') === 0
            ? 'Network error. Please try again.'
            : 'Erreur réseau. Merci de réessayer.');
        });
      });
    }

    function onSuccess() {
      form.hidden = true;
      successPane.hidden = false;
      try { localStorage.setItem(STORAGE_KEY_SUBSCRIBED, String(Date.now())); }
      catch (e) { /* noop */ }
      // Hide the sticky trigger since the user has subscribed
      wrapper.classList.remove('np--visible');

      // Notify any listeners (analytics, Klaviyo _learnq, etc.)
      try {
        window._learnq = window._learnq || [];
        window._learnq.push(['identify', { $email: input.value.trim() }]);
        window._learnq.push(['track', 'Signed Up for Newsletter', {
          source: 'popup-first-purchase-discount'
        }]);
      } catch (e) { /* noop */ }
    }

    function onError(text) {
      submitBtn.disabled = false;
      msg.textContent = text;
      msg.setAttribute('data-state', 'error');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNewsletterPopup);
  } else {
    initNewsletterPopup();
  }
})();
